import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Camera, User, Briefcase, Mail, Calendar, Link as LinkIcon } from "lucide-react";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Avatar } from "@/components/ui/Avatar";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectPopup,
  SelectItem,
} from "@/components/ui/Select";
import { toProperCase } from "src/utils/Strings";
import { updateProfileApi } from "src/api";
import { UserAccountState } from "src/enums/shared.enums";
import { useProfile } from "src/hooks";
import { useProfilePicture } from "src/hooks/profile/useProfilePicture";
import { useOptimisticUpdate } from "src/hooks/common/useOptimisticUpdate";
import { useDemoOrg } from "src/contexts/DemoOrgContext";
import { notifyFeature } from "src/utils/demoNotifications";
import DEFAULT_UNKNOWN_PROFILE_PICTURE from "src/assets/img/DefaultUnknownProfilePicture.png";

interface ProfileProps {
  isDemo?: boolean;
}

const ACCOUNT_TYPES = ["artist", "manager", "producer", "publisher"] as const;

const Profile: React.FC<ProfileProps> = ({ isDemo = false }) => {
  const navigate = useNavigate();
  const demoOrgContext = useDemoOrg();

  // Profile data
  const { profile, updateProfileCache, loading } = useProfile();
  const { profilePictureURL } = useProfilePicture();
  const { withOptimisticUpdate } = useOptimisticUpdate();

  // Demo profile
  const demoProfile = isDemo && demoOrgContext ? demoOrgContext.getCurrentProfile() : null;
  const demoImageUrl = demoProfile?.profileImage || "/clipse.webp";

  const displayImageUrl = isDemo ? demoImageUrl : (profilePictureURL || DEFAULT_UNKNOWN_PROFILE_PICTURE);

  const [isUpdating, setIsUpdating] = useState(false);

  const updateProfileTrigger = useCallback(
    async (formData: any) => {
      if (!profile) return;

      setIsUpdating(true);
      const previousProfile = { ...profile };

      try {
        const response = await withOptimisticUpdate(
          () => updateProfileApi(formData),
          () => {
            const optimisticProfile = { ...profile, ...formData };
            updateProfileCache(optimisticProfile);
          },
          () => {
            updateProfileCache(previousProfile);
          }
        );
        return response;
      } finally {
        setIsUpdating(false);
      }
    },
    [profile, updateProfileCache, withOptimisticUpdate]
  );

  // Form setup
  const defaultValues = useMemo(() => {
    if (isDemo && demoProfile) {
      return {
        firstName: demoProfile.displayName?.split(" ")[0] || "Parker",
        lastName: demoProfile.displayName?.split(" ").slice(1).join(" ") || "Van Roy",
        displayName: demoProfile.displayName || "Parker Van Roy",
        accountType: "manager",
        bio: "Managing artists and deals with EqualIQ.",
        state: UserAccountState.ONBOARDED,
      };
    }

    if (profile) {
      return {
        firstName: profile.firstName || "",
        lastName: profile.lastName || "",
        displayName: profile.displayName || "",
        accountType: profile.accountType || "artist",
        bio: profile.bio || "",
        state: profile.state || UserAccountState.ONBOARDED,
      };
    }

    return {
      firstName: "",
      lastName: "",
      displayName: "",
      accountType: "artist",
      bio: "",
      state: UserAccountState.ONBOARDED,
    };
  }, [profile, isDemo, demoProfile]);

  const { control, reset, handleSubmit, formState, watch } = useForm({
    defaultValues,
  });
  const { dirtyFields } = formState;

  useEffect(() => {
    if (isDemo && demoProfile) {
      reset({
        firstName: demoProfile.displayName?.split(" ")[0] || "Parker",
        lastName: demoProfile.displayName?.split(" ").slice(1).join(" ") || "Van Roy",
        displayName: demoProfile.displayName || "Parker Van Roy",
        accountType: "manager",
        bio: "Managing artists and deals with EqualIQ.",
        state: UserAccountState.ONBOARDED,
      });
    } else if (profile) {
      reset({
        firstName: profile.firstName || "",
        lastName: profile.lastName || "",
        displayName: profile.displayName || "",
        accountType: profile.accountType || "artist",
        bio: profile.bio || "",
        state: profile.state || UserAccountState.ONBOARDED,
      });
    }
  }, [profile, isDemo, demoProfile, reset]);

  const onSubmit = async (formData: any) => {
    if (isDemo) {
      notifyFeature("Update profile", isDemo);
      return;
    }

    const dirtyFieldsKeys = Object.keys(dirtyFields);
    if (dirtyFieldsKeys.length === 0) {
      toast.info("No changes to save");
      return;
    }

    const changedData: any = { state: formData.state };
    dirtyFieldsKeys.forEach((key) => {
      if (key !== "isOver18" && key !== "state") {
        changedData[key] = formData[key];
      }
    });

    try {
      await updateProfileTrigger(changedData);
      toast.success("Profile updated successfully!");
      reset(formData, { keepValues: true });
    } catch (error) {
      toast.error("Failed to update profile");
      console.error("Error updating profile:", error);
    }
  };

  const displayName = watch("displayName");
  const resolvedEmail = isDemo
    ? (demoProfile?.email || "parker@equal-iq.com")
    : (profile?.email || "");

  // Loading state
  if (!isDemo && loading && !profile) {
    return (
      <div className="min-h-screen">
        <div className="max-w-2xl mx-auto px-6 pt-32 pb-12">
          <div className="text-center py-16">
            <div className="animate-pulse text-white/40">Loading profile...</div>
          </div>
        </div>
      </div>
    );
  }

  const hasDirtyFields = Object.keys(dirtyFields).length > 0;

  return (
    <div className="min-h-screen">
      <div className="max-w-2xl mx-auto px-6 pt-32 pb-12">
        {/* Header */}
        <div className="mb-10">
          <button
            className="text-white bg-transparent border-none cursor-pointer p-2 rounded-md flex items-center mb-4 hover:bg-white/10 transition-colors"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="size-4" />
          </button>

          <h1 className="text-3xl font-semibold text-white mb-2">Profile</h1>
          <p className="text-white/40">Manage your account details and preferences</p>
        </div>

        {/* Profile Card */}
        <div className="rounded-2xl border border-white/10 bg-white/5 p-6 mb-8">
          <div className="flex items-center gap-4">
            <div className="relative group">
              <Avatar
                src={displayImageUrl}
                alt={displayName || "Profile"}
                className="size-16"
              />
              <button
                className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
                onClick={() => isDemo ? notifyFeature("Change profile picture", isDemo) : notifyFeature("Change profile picture", false)}
              >
                <Camera className="size-4 text-white" />
              </button>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">
                {displayName || "Your Name"}
              </h2>
              <p className="text-sm text-white/40">{resolvedEmail}</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Basic Information */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4 px-1">
              <User className="size-4 text-white/40" />
              <h2 className="text-white text-lg font-semibold">Basic Information</h2>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/5 p-6 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label htmlFor="firstName" className="text-sm text-white/60">
                    First Name
                  </label>
                  <Controller
                    name="firstName"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        id="firstName"
                        placeholder="First Name"
                        className="border-white/10 bg-white/5"
                      />
                    )}
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="lastName" className="text-sm text-white/60">
                    Last Name
                  </label>
                  <Controller
                    name="lastName"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        id="lastName"
                        placeholder="Last Name"
                        className="border-white/10 bg-white/5"
                      />
                    )}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="displayName" className="text-sm text-white/60">
                  Display Name
                </label>
                <Controller
                  name="displayName"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      id="displayName"
                      placeholder="Display Name"
                      className="border-white/10 bg-white/5"
                    />
                  )}
                />
              </div>
            </div>
          </div>

          {/* Account Details */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4 px-1">
              <Briefcase className="size-4 text-white/40" />
              <h2 className="text-white text-lg font-semibold">Account Details</h2>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/5 p-6 space-y-5">
              <div className="space-y-2">
                <label className="text-sm text-white/60">
                  Account Type
                </label>
                <Controller
                  name="accountType"
                  control={control}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger className="border-white/10 bg-white/5">
                        <SelectValue>
                          {toProperCase(field.value)}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectPopup>
                        {ACCOUNT_TYPES.map((type) => (
                          <SelectItem key={type} value={type}>
                            {toProperCase(type)}
                          </SelectItem>
                        ))}
                      </SelectPopup>
                    </Select>
                  )}
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="bio" className="text-sm text-white/60">
                  Bio
                </label>
                <Controller
                  name="bio"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      id="bio"
                      placeholder="Tell us about yourself..."
                      className="border-white/10 bg-white/5"
                    />
                  )}
                />
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              type="submit"
              variant="default"
              size="lg"
              disabled={!isDemo && (!hasDirtyFields || isUpdating)}
            >
              {isUpdating ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>

        {/* Connected Accounts */}
        <div className="mt-12 mb-8">
          <div className="flex items-center gap-2 mb-4 px-1">
            <LinkIcon className="size-4 text-white/40" />
            <h2 className="text-white text-lg font-semibold">Connected Accounts</h2>
          </div>
          <p className="text-white/40 text-sm mb-4 px-1">
            Connect your email and calendar to sync conversations and track meetings.
          </p>

          <div className="space-y-3">
            {/* Email */}
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4 flex items-center gap-4">
              <div className="size-10 rounded-xl bg-white/8 grid place-items-center shrink-0">
                <Mail className="size-5 text-white/60" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white">Email</p>
                <p className="text-xs text-white/40">Sync your inbox to discover deals and track conversations</p>
              </div>
              <span className="text-xs text-white/30 shrink-0 hidden sm:block">via Nylas</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => toast.info("Email connection coming soon")}
              >
                Connect
              </Button>
            </div>

            {/* Calendar */}
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4 flex items-center gap-4">
              <div className="size-10 rounded-xl bg-white/8 grid place-items-center shrink-0">
                <Calendar className="size-5 text-white/60" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white">Calendar</p>
                <p className="text-xs text-white/40">Sync your calendar for meeting scheduling and deadline tracking</p>
              </div>
              <span className="text-xs text-white/30 shrink-0 hidden sm:block">via Nylas</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => toast.info("Calendar connection coming soon")}
              >
                Connect
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
